import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ExternalLinkIcon,
  FileTextIcon,
  FolderOffIcon,
  GraphicEqIcon,
  LinkIcon,
  SubtitlesOffIcon,
} from '../icons.js';
import { apiGet } from '../../api/client.js';
import { vfmt } from '../../lib/video-format.js';
import type { VideoCardData } from './VideoCard.js';

/**
 * Tab bodies for the VideoDetailPage:
 *   - ChaptersList   — clickable list of chapter rows; active row highlights.
 *   - TranscriptView — fetches transcript.json from the backend, renders
 *                      timestamped segments. Empty state for silent videos.
 *   - ResourcesList  — linked docs / external URLs attached to the video.
 *   - SeriesList     — every episode of the series the video belongs to,
 *                      with a "Now playing" chip on the current one.
 */

export interface Chapter {
  index: number;
  title: string;
  startSeconds: number;
  endSeconds: number;
}

interface ChaptersListProps {
  chapters: Chapter[];
  currentT: number;
  onJump: (seconds: number) => void;
}

export function ChaptersList({ chapters, currentT, onJump }: ChaptersListProps): JSX.Element {
  if (chapters.length === 0) {
    return (
      <div className="mt-2 rounded-md border border-nebula-border-soft bg-white p-6 text-center text-sm text-nebula-fg-3">
        Chapters are produced by the ingest pipeline; this video doesn't have any yet.
      </div>
    );
  }
  return (
    <ol className="flex list-none flex-col gap-1.5 p-0">
      {chapters.map((c, i) => {
        const next = chapters[i + 1];
        const active = currentT >= c.startSeconds && (!next || currentT < next.startSeconds);
        return (
          <li key={c.index}>
            <button
              onClick={() => onJump(c.startSeconds)}
              className={
                active
                  ? 'flex w-full items-center gap-3.5 rounded-md border border-l-[3px] border-[#DCE5FB] border-l-nebula-secondary bg-nebula-tint px-3.5 py-3 text-left'
                  : 'flex w-full items-center gap-3.5 rounded-md border border-l-[3px] border-nebula-border-soft border-l-transparent bg-white px-3.5 py-3 text-left transition hover:border-l-nebula-secondary hover:bg-nebula-tint'
              }
            >
              <span
                className={
                  active
                    ? 'inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-nebula-secondary text-[12px] font-bold text-white'
                    : 'inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-nebula-gray-5 text-[12px] font-bold text-nebula-fg-3'
                }
              >
                {i + 1}
              </span>
              <div className="min-w-0 flex-1">
                <div className="text-[13.5px] font-semibold text-nebula-fg-1">{c.title}</div>
              </div>
              <span
                className={
                  active
                    ? 'rounded-md bg-white px-2 py-1 text-xs font-semibold text-nebula-primary [font-variant-numeric:tabular-nums]'
                    : 'text-xs font-semibold text-nebula-fg-4 [font-variant-numeric:tabular-nums]'
                }
              >
                {vfmt(c.startSeconds)}
              </span>
              {active && <GraphicEqIcon className="w-4 h-4 text-nebula-secondary" />}
            </button>
          </li>
        );
      })}
    </ol>
  );
}

interface TranscriptSegment {
  t: number;
  speaker?: string;
  text: string;
}

interface TranscriptViewProps {
  videoId: string;
  currentT: number;
  onJump: (seconds: number) => void;
}

export function TranscriptView({ videoId, currentT, onJump }: TranscriptViewProps): JSX.Element {
  const [segments, setSegments] = useState<TranscriptSegment[] | null>(null);
  const [audioPresent, setAudioPresent] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setSegments(null);
    setError(null);
    (async () => {
      try {
        const data = await apiGet<{ segments: TranscriptSegment[]; audioPresent: boolean }>(
          `/api/v1/videos/${videoId}/transcript`,
        );
        setSegments(data.segments);
        setAudioPresent(data.audioPresent);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Could not load transcript');
      }
    })();
  }, [videoId]);

  if (error) {
    return (
      <div className="mt-2 rounded-md border border-nebula-error bg-nebula-error-bg p-4 text-sm text-nebula-error">
        {error}
      </div>
    );
  }
  if (!audioPresent) {
    return (
      <EmptyTab
        icon={<SubtitlesOffIcon className="w-7 h-7" />}
        title="Silent video"
        subtitle="This recording has no audio track. Use chapters or visual summaries instead."
      />
    );
  }
  if (segments === null) {
    return <div className="mt-2 text-sm text-nebula-fg-3">Loading transcript…</div>;
  }
  if (segments.length === 0) {
    return (
      <EmptyTab
        icon={<SubtitlesOffIcon className="w-7 h-7" />}
        title="Transcript not available yet"
        subtitle="Transcripts are generated within minutes of ingest completing. Try Chapters in the meantime."
      />
    );
  }
  return (
    <div className="rounded-md border border-nebula-border-soft bg-white p-4">
      <div className="flex flex-col gap-3">
        {segments.map((seg, i) => {
          const next = segments[i + 1];
          const active = currentT >= seg.t && (!next || currentT < next.t);
          return (
            <div
              key={i}
              className={
                active
                  ? 'grid gap-3.5 rounded-md bg-nebula-tint px-2 py-1'
                  : 'grid gap-3.5 px-2 py-1'
              }
              style={{ gridTemplateColumns: '52px 1fr' }}
            >
              <button
                onClick={() => onJump(seg.t)}
                className={
                  active
                    ? 'border-0 bg-transparent py-0.5 text-left text-xs font-semibold text-nebula-primary [font-variant-numeric:tabular-nums]'
                    : 'border-0 bg-transparent py-0.5 text-left text-xs font-semibold text-nebula-fg-4 [font-variant-numeric:tabular-nums]'
                }
              >
                {vfmt(seg.t)}
              </button>
              <div>
                {seg.speaker && (
                  <div className="mb-0.5 text-[11px] font-semibold uppercase tracking-widest text-nebula-fg-4">
                    {seg.speaker}
                  </div>
                )}
                <div className="text-[13.5px] leading-[20px] text-nebula-fg-1">{seg.text}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export interface VideoResource {
  kind: 'doc' | 'link';
  title: string;
  documentId?: string;
  href?: string;
}

export function ResourcesList({ resources }: { resources: VideoResource[] }): JSX.Element {
  if (resources.length === 0) {
    return (
      <EmptyTab
        icon={<FolderOffIcon className="w-7 h-7" />}
        title="No attached resources"
        subtitle="The presenter hasn't linked any documents or external pages to this video."
      />
    );
  }
  return (
    <div className="flex flex-col gap-2">
      {resources.map((r, i) => {
        const isDoc = r.kind === 'doc';
        // Doc resources open the source file directly via the agent's
        // sources route (same pattern as KnowledgeAgent citation cards) —
        // /documents doesn't support a deep-link query param, so a link
        // there would just dump the user onto the full corpus index.
        const target = isDoc
          ? `/api/v1/agent/sources/${encodeURIComponent(r.documentId ?? '')}`
          : (r.href ?? '#');
        // Both doc downloads and external links open in a new tab.
        const external = isDoc || (r.href ? !r.href.startsWith('/') : false);
        const inner = (
          <>
            <span
              className={
                isDoc
                  ? 'inline-flex h-9 w-9 items-center justify-center rounded-md bg-nebula-error-bg text-nebula-error'
                  : 'inline-flex h-9 w-9 items-center justify-center rounded-md bg-nebula-tint text-nebula-secondary'
              }
            >
              {isDoc ? <FileTextIcon className="w-5 h-5" /> : <LinkIcon className="w-5 h-5" />}
            </span>
            <div className="min-w-0 flex-1">
              <div className="text-[13.5px] font-semibold text-nebula-fg-1">{r.title}</div>
              {isDoc && r.documentId && (
                <div className="font-mono text-xs text-nebula-fg-3">{r.documentId}</div>
              )}
            </div>
            <ExternalLinkIcon className="w-4 h-4 text-nebula-fg-3" />
          </>
        );
        if (external) {
          return (
            <a
              key={i}
              href={target}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3.5 rounded-md border border-nebula-border-soft bg-white px-3.5 py-3 text-inherit no-underline hover:bg-nebula-tint"
            >
              {inner}
            </a>
          );
        }
        return (
          <Link
            key={i}
            to={target}
            className="flex items-center gap-3.5 rounded-md border border-nebula-border-soft bg-white px-3.5 py-3 text-inherit no-underline hover:bg-nebula-tint"
          >
            {inner}
          </Link>
        );
      })}
    </div>
  );
}

interface SeriesListProps {
  series: { id: string; title: string; subtitle?: string };
  episodes: VideoCardData[];
  currentId: string;
}

export function SeriesList({ series, episodes, currentId }: SeriesListProps): JSX.Element {
  return (
    <div>
      <div className="mb-3 rounded-md border border-[#DCE5FB] bg-nebula-tint px-4 py-3.5">
        <div className="text-sm font-bold text-nebula-primary">{series.title}</div>
        {series.subtitle && (
          <div className="mt-0.5 text-xs text-nebula-fg-3">{series.subtitle}</div>
        )}
      </div>
      <div className="flex flex-col gap-2">
        {episodes.map((v) => {
          const on = v.id === currentId;
          return (
            <div
              key={v.id}
              className={
                on
                  ? 'flex items-center gap-3.5 rounded-md border border-nebula-primary bg-white p-2.5 opacity-100'
                  : 'flex items-center gap-3.5 rounded-md border border-nebula-border-soft bg-white p-2.5 opacity-100 hover:bg-nebula-tint'
              }
            >
              <Link
                to={`/videos/${v.id}`}
                className="flex flex-1 items-center gap-3.5 text-inherit no-underline"
              >
                <div
                  style={{
                    flex: '0 0 132px',
                    height: 74,
                    borderRadius: 6,
                    background: '#172E57',
                    position: 'relative',
                  }}
                >
                  <span className="absolute bottom-1 right-1 rounded-sm bg-black/80 px-1.5 py-0.5 text-[10.5px] font-semibold text-white [font-variant-numeric:tabular-nums]">
                    {vfmt(v.durationSeconds ?? 0)}
                  </span>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="mb-1 text-[11px] font-semibold uppercase tracking-widest text-nebula-fg-4">
                    Episode {v.episode ?? '?'} of {v.totalEpisodes ?? '?'}
                  </div>
                  <div className="text-sm font-semibold text-nebula-fg-1">{v.title}</div>
                  {v.presenter && (
                    <div className="mt-0.5 text-xs text-nebula-fg-3">{v.presenter.name}</div>
                  )}
                </div>
              </Link>
              {on && (
                <span className="rounded-full bg-nebula-primary px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider text-white">
                  Now playing
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function EmptyTab({
  icon,
  title,
  subtitle,
}: {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
}): JSX.Element {
  return (
    <div className="mt-2 flex flex-col items-center rounded-md border border-nebula-border-soft bg-white p-8 text-center">
      <div className="mb-3 inline-flex h-12 w-12 items-center justify-center rounded-full bg-nebula-gray-5 text-nebula-fg-3">
        {icon}
      </div>
      <div className="mb-1 text-sm font-semibold text-nebula-fg-1">{title}</div>
      <div className="max-w-md text-xs text-nebula-fg-3">{subtitle}</div>
    </div>
  );
}
